import React, { Fragment, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import {
  EditPlan,
  PlanImport,
  DeletePlan,
} from "../../../assets/RiskManagementIcon";
import { Box, Button, Container, Stack } from "@mui/material";
import { CreateNewIcon } from "../../../assets/RiskManagementIcon";
import DialogBoxComponent from "../../../components/DialogBoxComponent";
import RichTextBoxEditer from "../../../components/RichTextBoxEditer";
import { CircularProgress, ListItem, Tooltip } from "@mui/material";
import { useUserContext } from "../../../context/auth/UserContext";
import { useRiskManagementContext } from "../../../context/RiskManagement/RiskManagementContext";
import {
  deleteAppendix,
  fetchAppendixDetails,
  downloadAppendix,
  updateAppendix,
} from "./helper";
import { useAccessRights } from "../../../utils/useAccessRights";
import { getUserName } from "../../../helper/helper";

export const DisplayAppendixContent = (props: { refreshList: () => void }) => {
  const navigate = useNavigate();
  const riskManagementPageAccess = useAccessRights("Risk Management - CS & SC");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openDeleteConfirmationDialog: false,
    openSuccessDialogBox: false,
  });

  const { userId, companyProfileID } = useUserContext();
  const { tabValue, selectedAppendix, appendixDetails, setAppendixDetails } =
    useRiskManagementContext();

  const isSelected = () => {
    return (
      selectedAppendix?.csRiskManagementPlanAppendixID ||
      selectedAppendix?.scRiskManagementPlanAppendixID
    );
  };

  const displayEditAppendix = async (details: any) => {
    const date = new Date();
    const payload = {
      ...details,
      isEditedByUser: true,
      isEditedByUserId: userId + "",
      updateBy: userId,
      updateDate: date,
    };

    const resUpdate = await updateAppendix(tabValue === 0, payload);
    if (resUpdate.succeeded === true) {
      sessionStorage.setItem("allowAppendixForm", "true");
      navigate("/risk-management-appendix-form");
    }
  };

  const handleEditAppendix = async () => {
    const res = await fetchAppendixDetails(
      tabValue === 0,
      tabValue === 0
        ? selectedAppendix.csRiskManagementPlanAppendixID
        : selectedAppendix.scRiskManagementPlanAppendixID
    );

    // console.log("handleEditPlan plan", res);
    if (res.isEditedByUser && parseInt(res.isEditedByUserId) !== userId) {
      let userName = await getUserName(res.isEditedByUserId);
      setDialogState({
        title: "Info",
        body: `You are unable to modify this plan because ${userName} has already begun editing it.`,
        action: "Ok",
        actionCancel: "",
        openDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    } else {
      displayEditAppendix(res);
    }
  };

  const handleDialogClose = () => {
    setDialogState({
      ...dialogState,
      openDeleteConfirmationDialog: false,
      openSuccessDialogBox: false,
    });
    if (dialogState.title !== "Error") {
      props.refreshList();
    }
  };

  const handleDownloadAppendix = () => {
    if (isSelected()) {
      const res = downloadAppendix(
        tabValue === 0,
        selectedAppendix,
        companyProfileID
      );
      // console.log("handleDownloadAppendix res",selectedAppendix , res);
    }
  };

  const confirmDelete = () => {
    setDialogState({
      title: "Confirmation",
      body: `Are you sure you want to delete <strong>${appendixDetails.title}</strong> appendix?`,
      action: "Delete",
      actionCancel: "Cancel",
      openDeleteConfirmationDialog: true,
      openSuccessDialogBox: false,
    });
  };

  const handleDeleteAppendix = async () => {
    setAppendixDetails({});
    setDialogState(() => ({
      ...dialogState,
      openDeleteConfirmationDialog: false,
      openSuccessDialogBox: false,
    }));

    const res = await deleteAppendix(
      tabValue === 0,
      userId,
      tabValue === 0
        ? selectedAppendix.csRiskManagementPlanAppendixID
        : selectedAppendix.scRiskManagementPlanAppendixID
    );
    // console.log("handleDeleteAppendix res", payload, res);
    if (res.succeeded === true) {
      setDialogState({
        title: "Success",
        body: "Appendix successfully deleted",
        action: "OK",
        actionCancel: "",
        openDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    }
  };

  const handleFetchPlanDetails = async () => {
    try {
      setIsLoading(true);
      const res = await fetchAppendixDetails(
        tabValue === 0,
        tabValue === 0
          ? selectedAppendix.csRiskManagementPlanAppendixID
          : selectedAppendix.scRiskManagementPlanAppendixID
      );
      setAppendixDetails(res);
    } catch (error) {
      // console.log("@@Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isSelected()) {
      handleFetchPlanDetails();
    }
  }, [selectedAppendix]);

  return (
    <Fragment>
      <Grid
        container
        display="flex"
        width="100%"
        flexDirection="column"
        sx={{ height: "100%", width: "100%" }}
      >
        <Grid xs={2} display="flex" width="100%" flexDirection="row">
          <Stack
            display="flex"
            flexDirection="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              sx={{
                color: "#292D32",
                fontSize: "15px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "normal",
              }}
            >
              {appendixDetails.title}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Download Appendix">
                <IconButton
                  aria-label="download"
                  disabled={
                    !isSelected() || !riskManagementPageAccess?.isDownload
                  }
                  onClick={handleDownloadAppendix}
                >
                  {React.createElement(PlanImport, {
                    opacity:
                      !isSelected() || !riskManagementPageAccess?.isDownload
                        ? "50%"
                        : "100%",
                  })}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Appendix">
                <IconButton
                  aria-label="edit"
                  onClick={handleEditAppendix}
                  disabled={
                    !isSelected() || !riskManagementPageAccess?.isUpdate
                  }
                >
                  {React.createElement(EditPlan, {
                    opacity:
                      !isSelected() || !riskManagementPageAccess?.isUpdate
                        ? "50%"
                        : "100%",
                  })}
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Appendix">
                <IconButton
                  aria-label="delete"
                  onClick={confirmDelete}
                  disabled={
                    !isSelected() || !riskManagementPageAccess?.isDelete
                  }
                >
                  {React.createElement(DeletePlan, {
                    opacity:
                      !isSelected() || !riskManagementPageAccess?.isDelete
                        ? "50%"
                        : "100%",
                  })}
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Grid>

        <Grid xs mt={2}>
          {isLoading ? (
            <ListItem
              sx={{ width: "100%", display: "flex", flexDirection: "column" }}
            >
              <CircularProgress />
            </ListItem>
          ) : (
            <RichTextBoxEditer
              readOnly={true}
              editorContent={
                appendixDetails.content ? appendixDetails.content : ""
              }
              onChange={() => {}}
            />
          )}
        </Grid>
      </Grid>
      {
        <DialogBoxComponent
          openDialogBox={dialogState.openDeleteConfirmationDialog}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={handleDeleteAppendix}
          handleCancel={handleDialogClose}
        />
      }
      {
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleDialogClose}
        />
      }
    </Fragment>
  );
};
